import {
  IUnifiedBackgroundCheckDetail,
  IUnifiedCriminalRecord,
  IUnifiedDate,
  IUnifiedDriversLicense,
  IUnifiedMvrViolation,
  IUnifiedProfessionalLicense,
  IUnifiedSharedSsnIndividual,
  IUnifiedWatchListRecord,
  UnifiedBackgroundCheckSource
} from '../../core/interfaces/unified-background-check.interface';
import {
  IBackgroundCheckViewModel,
  ICardBadge,
  ICardField,
  IDriversLicenseGroup,
  IIdentityViewModel,
  IMetric,
  IRecordCard,
  IReviewItem,
  ISidebarSection,
  SidebarBadgeType
} from './driver-background-check.view-models';
import {
  SECTION_AVAILABILITY,
  resolveStatusColor
} from './driver-background-check.constants';
import { formatAddress, formatDate, formatName } from '../helpers';

const EMPTY_SUBJECT_MATCH = {
  name: '—', ssn: '', dob: '—', driverLicense: '', email: '', phone: '', result: '—', assessment: '—'
};

const SIDEBAR_TEMPLATE: ISidebarSection[] = [
  { id: 'summary', label: 'Summary', count: 0, findings: 0, icon: 'fa-clipboard-list', badgeType: 'none' },
  { id: 'identity', label: 'Identity & SSN Trace', count: 0, findings: 0, icon: 'fa-id-card', badgeType: 'none' },
  { id: 'licenses', label: 'Driver\'s Licenses', count: 0, findings: 0, icon: 'fa-address-card', badgeType: 'none' },
  { id: 'mvr', label: 'Motor Vehicle Report', count: 0, findings: 0, icon: 'fa-car', badgeType: 'none' },
  { id: 'criminal', label: 'Criminal Background Check', count: 0, findings: 0, icon: 'fa-gavel', badgeType: 'none' },
  { id: 'watchlist', label: 'Watch List', count: 0, findings: 0, icon: 'fa-user-shield', badgeType: 'none' },
  { id: 'documents', label: 'Documents', count: 0, findings: 0, icon: 'fa-file-pdf', badgeType: 'none' },
  { id: 'permits', label: 'Licenses & Permits', count: 0, findings: 0, icon: 'fa-id-badge', badgeType: 'none' },
  { id: 'details', label: 'Report Details', count: 0, findings: 0, icon: 'fa-info-circle', badgeType: 'none' },
];

interface ISectionCount {
  count: number;
  findings: number;
}

export class BackgroundCheckCardBuilder {
  constructor(
    private detail: IUnifiedBackgroundCheckDetail,
    private source: UnifiedBackgroundCheckSource
  ) {}

  build(): IBackgroundCheckViewModel {
    if (!this.detail) {
      return EMPTY_BACKGROUND_CHECK_VIEW_MODEL;
    }

    const criminalRecords = this.detail.criminalrecords || [];
    const watchListRecords = this.detail.watchlistrecords || [];
    const driversLicenses = this.detail.driverslicenses || [];
    const professionalLicenses = this.detail.professionallicenses || [];
    const individualsSharingSsn = this.detail.individualssharingssn || [];
    const motorVehicleReport = this.detail.motorvehiclereport || null;
    const mvrViolations = (motorVehicleReport && motorVehicleReport.violations) || [];
    const mvrAccidents = (motorVehicleReport && motorVehicleReport.accidents) || [];

    const criminalCards = this.buildCriminalCards(criminalRecords);
    const driversLicenseCards = this.buildDriversLicenseCards(driversLicenses);
    const driversLicenseGroups = this.buildDriversLicenseGroups(driversLicenseCards);
    const sharedSsnCards = this.buildSharedSsnCards(individualsSharingSsn);
    const professionalLicenseCards = this.buildProfessionalLicenseCards(professionalLicenses);
    const watchListCards = this.buildWatchListCards(watchListRecords);
    const mvrViolationCards = mvrViolations.map((violation, index) => this.buildMvrCard(`mvr-violation-${index}`, violation));
    const mvrAccidentCards = mvrAccidents.map((accident, index) => this.buildMvrCard(`mvr-accident-${index}`, accident));
    const documents = (this.detail.documents || []).filter((d) => d.type !== 'pdf_report');

    const identity = this.buildIdentity(individualsSharingSsn);
    const subjectMatch = this.buildSubjectMatch(identity, driversLicenses);
    const reportDetails = {
      reportToken: this.detail.reporttoken,
      searchType: this.source === 'CheckR' ? 'CheckR Background Check' : 'Comprehensive Person Report',
      reportDate: this.detail.reportdate,
      source: this.source === 'CheckR' ? 'CheckR API' : 'SearchBug API',
      status: this.detail.status,
      result: this.detail.result,
      assessment: this.detail.assessment,
      completedAt: this.detail.reportdate,
    };

    const identityCount =
      identity.names.length + identity.dobs.length + identity.addresses.length + identity.phones.length + identity.emails.length;
    const identityFindings = identity.individualsSharingSsn.length;

    const criminalCount = criminalCards.length;
    const watchlistCount = watchListCards.length;
    const licensesCount = driversLicenseCards.length;
    const permitsCount = professionalLicenseCards.length;
    const mvrCount = mvrViolations.length + mvrAccidents.length;
    const documentsCount = documents.length;

    const expiredLicensesCount = driversLicenseCards.filter((card) => card.isExpired).length;
    const hasValidLicense = driversLicenseCards.some((card) => !card.isExpired);
    const shouldFlagExpiredLicenses = expiredLicensesCount > 0 && !(this.source === 'SearchBug' && hasValidLicense);

    const criminalFindings = this.isReviewFindingByRecords(criminalCount, criminalRecords) ? criminalCount : 0;
    const watchlistFindings = this.isReviewFindingByRecords(watchlistCount, watchListRecords) ? watchlistCount : 0;
    const licensesFindings = shouldFlagExpiredLicenses ? expiredLicensesCount : 0;
    const permitsFindings = 0;
    const mvrFindings = this.isReviewFinding(mvrCount) ? mvrCount : 0;
    const documentsFindings = 0;

    const availableSectionsCount = SIDEBAR_TEMPLATE.filter((s) => this.isSectionAvailable(s.id)).length;

    const metrics = this.buildMetrics({
      availableSectionsCount, criminalCount, watchlistCount, mvrCount, licensesCount,
      mvrAccidentsCount: mvrAccidents.length, documentsCount,
    });
    const itemsRequiringReview = this.buildItemsRequiringReview({
      identityFindings, criminalCount, criminalFindings, watchlistCount, watchlistFindings,
      mvrCount, mvrFindings, shouldFlagExpiredLicenses, expiredLicensesCount,
    });

    const sidebarSections = this.buildSidebarSections({
      identity: { count: identityCount, findings: identityFindings },
      criminal: { count: criminalCount, findings: criminalFindings },
      watchlist: { count: watchlistCount, findings: watchlistFindings },
      licenses: { count: licensesCount, findings: licensesFindings },
      permits: { count: permitsCount, findings: permitsFindings },
      mvr: { count: mvrCount, findings: mvrFindings },
      documents: { count: documentsCount, findings: documentsFindings },
    });

    return {
      criminalCards,
      driversLicenseCards,
      driversLicenseGroups,
      sharedSsnCards,
      professionalLicenseCards,
      watchListCards,
      mvrViolationCards,
      mvrAccidentCards,
      documents,
      metrics,
      itemsRequiringReview,
      subjectMatch,
      identity,
      reportDetails,
      sidebarSections,
    };
  }

  private buildCriminalCards(records: IUnifiedCriminalRecord[]): IRecordCard[] {
    return records.map((record, index) => {
      if (record.searchtype) {
        return this.buildCheckrRecordCard(`criminal-${index}`, record.searchtype, record.jurisdiction, record.status, record.result);
      }
      const filed = record.chargesfileddate || record.arrestdate;
      const fields: ICardField[] = [
        { label: 'Case', value: record.casenumber || '—' },
        { label: 'Classification', value: record.classification || '—' },
        { label: 'Disposition', value: record.disposition || '—' },
        { label: 'Arresting Agency', value: record.arrestingagency || '—' },
        { label: 'Disposition Date', value: formatDate(record.dispositiondate) },
        { label: 'Filed Date', value: formatDate(filed) },
        { label: 'Arrest Date', value: formatDate(record.arrestdate) },
      ];

      const suspect = {
        name: formatName(record.suspectname),
        address: formatAddress(record.suspectaddress),
        dl: record.suspectdriverslicensenumber,
        sexOffender: (record.suspectissexoffender || '').trim().toUpperCase() === 'Y' ? 'Yes' : 'No',
      };

      const dateSub = this.hasFullDate(record.dispositiondate)
        ? formatDate(record.dispositiondate)
        : this.hasFullDate(filed)
          ? formatDate(filed)
          : '';

      return {
        id: `criminal-${index}`,
        title: `${record.charge || '—'} — ${record.casenumber || '—'}`,
        sub: this.joinCardSub([record.jurisdiction, dateSub]),
        badge: this.criminalBadge(record.classification),
        fields,
        lines: [],
        suspect,
      };
    });
  }

  private buildDriversLicenseCards(driversLicenses: IUnifiedDriversLicense[]): IRecordCard[] {
    const licenses = driversLicenses.filter(
      (license) => this.source !== 'SearchBug' || this.hasFullDate(license.expirationdate)
    );
    const sorted = [...licenses].sort((a, b) => {
      const stateA = a.issuingstate || 'Unknown State';
      const stateB = b.issuingstate || 'Unknown State';
      return stateA.localeCompare(stateB);
    });
    let prevGroupState: string = null;
    return sorted.map((license, index) => {
      const hasExpiration = this.hasFullDate(license.expirationdate);
      const isExpired = this.isDateExpired(license.expirationdate);
      const badge: ICardBadge = !hasExpiration
        ? { text: 'RECORD', kind: 'info' }
        : isExpired
          ? { text: 'EXPIRED', kind: 'danger' }
          : { text: 'VALID', kind: 'success' };
      const fields: ICardField[] = this.omitEmptyFields([
        { label: 'License Number', value: license.licensenumber || '—', mask: 'license' },
        { label: 'State', value: license.issuingstate || '—' },
        { label: 'Name', value: formatName(license.name), mask: 'default' },
        { label: 'Expiration Date', value: formatDate(license.expirationdate), mask: 'default' },
        { label: 'Address', value: formatAddress(license.address), mask: 'default' },
      ]);
      const state = license.issuingstate || 'Unknown State';
      const showGroup = state !== prevGroupState;
      prevGroupState = state;
      return {
        id: `license-${index}`,
        title: `License ${state}`,
        sub: this.hasFullDate(license.datereported) ? `Reported ${formatDate(license.datereported)}` : '',
        badge,
        fields,
        group: state,
        showGroup,
        isExpired,
      };
    });
  }

  private buildDriversLicenseGroups(cards: IRecordCard[]): IDriversLicenseGroup[] {
    const groups: IDriversLicenseGroup[] = [];
    cards.forEach((card) => {
      if (card.showGroup || groups.length === 0) {
        groups.push({ state: card.group, cards: [card] });
      } else {
        groups[groups.length - 1].cards.push(card);
      }
    });
    return groups;
  }

  private buildSharedSsnCards(individuals: IUnifiedSharedSsnIndividual[]): IRecordCard[] {
    return individuals.map((item, index) => {
      const fields: ICardField[] = [
        { label: 'SSN', value: item.ssn || '—', mask: 'ssn' },
        { label: 'State', value: item.state || '—' },
        { label: 'Years', value: item.years || '—' },
        { label: 'DOB', value: formatDate(item.dob) },
      ];
      const badge: ICardBadge = { text: 'SHARED SSN', kind: 'danger' };
      return {
        id: `sharedssn-${index}`,
        title: formatName(item.name),
        sub: `DOB ${formatDate(item.dob)}`,
        badge,
        fields,
      };
    });
  }

  private buildProfessionalLicenseCards(licenses: IUnifiedProfessionalLicense[]): IRecordCard[] {
    return licenses.map((license, index) => {
      const fields: ICardField[] = [
        { label: 'License Number', value: license.licensenumber || '—', mask: 'license' },
        { label: 'Type', value: license.licensetype || '—' },
        { label: 'State', value: license.licensestate || '—' },
        { label: 'Status', value: license.licensestatus || '—' },
      ];
      return {
        id: `permit-${index}`,
        title: license.tradetype || 'Professional License',
        badge: this.professionalLicenseBadge(license.licensestatus),
        fields,
      };
    });
  }

  private buildWatchListCards(records: IUnifiedWatchListRecord[]): IRecordCard[] {
    return records.map((record, index) => {
      if (record.searchtype) {
        return this.buildCheckrRecordCard(`watchlist-${index}`, record.searchtype, null, record.status, record.result);
      }
      return {
        id: `watchlist-${index}`,
        title: record.watchlistentrysource || 'Watch List Hit',
        sub: record.fullname || '',
        badge: { text: 'HIT', kind: 'danger' } as ICardBadge,
        fields: [
          { label: 'Score', value: record.score || '—' },
          { label: 'Match', value: record.fullname || '—' },
          { label: 'Type', value: record.type || '—' },
          { label: 'Offense', value: record.offense || '—' },
          { label: 'Wanted By', value: record.wantedby || '—' },
          { label: 'Country', value: record.country || '—' },
          { label: 'Program', value: record.program || '—' },
        ],
      };
    });
  }

  private buildCheckrRecordCard(id: string, searchType: string, jurisdiction: string, status: string, result: string): IRecordCard {
    return {
      id,
      title: this.searchTypeTitle(searchType, jurisdiction),
      badge: this.checkrRecordBadge(status, result),
      fields: [
        { label: 'Status', value: status || '—' },
        { label: 'Result', value: result || '—' },
      ],
    };
  }

  private searchTypeTitle(searchType: string, jurisdiction: string): string {
    const labels: { [key: string]: string } = {
      global_watchlist_search: 'Global Watchlist Search',
      sex_offender_search: 'Sex Offender Search',
      terrorist_watchlist_search: 'Terrorist Watchlist Search',
      national_criminal_search: 'National Criminal Search',
      county_criminal_search: 'County Criminal Search',
    };
    const label = labels[searchType] || this.titleizeSearchType(searchType);
    return jurisdiction ? `${label} — ${jurisdiction}` : label;
  }

  private titleizeSearchType(searchType: string): string {
    const words = (searchType || '').split('_').filter((w) => !!w);
    if (!words.length) {
      return 'Search';
    }
    return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  private checkrRecordBadge(status: string, result: string): ICardBadge {
    const value = result || status;
    return { text: value || '—', kind: resolveStatusColor(value).badgeKind };
  }

  private buildMvrCard(id: string, item: IUnifiedMvrViolation): IRecordCard {
    const fields: ICardField[] = this.omitEmptyFields([
      { label: 'Disposition', value: item.disposition || '—' },
      { label: 'State', value: item.state || '—' },
      { label: 'Issued Date', value: formatDate(item.issueddate) },
      { label: 'ACD Code', value: item.acdcode || '—' },
    ]);
    return {
      id,
      title: item.description || 'Violation',
      fields,
    };
  }

  private buildIdentity(individualsSharingSsn: IUnifiedSharedSsnIndividual[]): IIdentityViewModel {
    const names = this.detail.names || [];
    const dobs = this.detail.dobs || [];
    const addresses = this.detail.addresses || [];
    const phones = this.detail.phones || [];
    const emails = this.detail.emails || [];
    const ssnTrace = this.detail.ssn || '—';
    const ssnHistory = this.detail.ssnhistory || [];
    const isClear =
      names.length === 0 &&
      ssnTrace === '—' &&
      dobs.length === 0 &&
      emails.length === 0 &&
      addresses.length === 0 &&
      phones.length === 0;
    return {
      names, dobs, addresses, phones, lastPhone: phones[phones.length - 1], emails,
      ssnTrace, ssnHistory, isClear, individualsSharingSsn,
    };
  }

  private buildSubjectMatch(identity: IIdentityViewModel, driversLicenses: IUnifiedDriversLicense[]) {
    const name = formatName(identity.names[0]);
    const dob = formatDate(identity.dobs[0]);
    const email = identity.emails[0] || '';
    const phone = (identity.phones[0] && identity.phones[0].phonenumber) || '';
    const ssn = this.detail.ssn || '';
    const driverLicense = (driversLicenses[0] && driversLicenses[0].licensenumber) || '';
    return {
      name, ssn, dob, driverLicense, email, phone,
      result: this.detail.result || '—',
      assessment: this.detail.assessment || '—',
    };
  }

  private buildMetrics(counts: {
    availableSectionsCount: number; criminalCount: number; watchlistCount: number; mvrCount: number;
    licensesCount: number; mvrAccidentsCount: number; documentsCount: number;
  }): IMetric[] {
    const goodOutcome = this.isGoodOutcome(this.detail.result);
    const allMetrics: IMetric[] = [
      {
        label: 'Report Result',
        value: this.detail.result || '—',
        icon: goodOutcome ? 'fa-check-circle' : 'fa-exclamation-circle',
        colorClass: goodOutcome ? 'metric-success' : 'metric-danger',
      },
      { label: 'Sections', value: counts.availableSectionsCount, icon: 'fa-list-ul', colorClass: 'metric-primary' },
      { label: 'Criminal Records', value: counts.criminalCount, icon: 'fa-balance-scale', colorClass: 'metric-danger' },
      { label: 'Watch List Hits', value: counts.watchlistCount, icon: 'fa-user-secret', colorClass: 'metric-danger' },
      {
        label: 'MVR Violations', value: counts.mvrCount, icon: 'fa-exclamation-triangle',
        colorClass: 'metric-warning', section: 'mvr'
      },
      { label: 'License Points', value: counts.licensesCount, icon: 'fa-award', colorClass: 'metric-warning' },
      {
        label: 'Accidents', value: counts.mvrAccidentsCount, icon: 'fa-car',
        colorClass: 'metric-success', section: 'mvr'
      },
      {
        label: 'Documents', value: counts.documentsCount, icon: 'fa-file-alt',
        colorClass: 'metric-primary', section: 'documents'
      },
    ];
    return allMetrics.filter((m) => !m.section || this.isSectionAvailable(m.section));
  }

  private buildItemsRequiringReview(params: {
    identityFindings: number; criminalCount: number; criminalFindings: number; watchlistCount: number;
    watchlistFindings: number; mvrCount: number; mvrFindings: number; shouldFlagExpiredLicenses: boolean;
    expiredLicensesCount: number;
  }): IReviewItem[] {
    const items: IReviewItem[] = [];
    if (params.identityFindings > 0) {
      items.push({
        severity: 'HIGH',
        title: 'SSN Shared With Another Individual',
        description: `${params.identityFindings} individual(s) also report this SSN.`,
        section: 'identity',
      });
    }
    if (params.criminalFindings > 0) {
      items.push({
        severity: 'HIGH',
        title: 'Criminal Records Found',
        description: `${params.criminalCount} criminal record(s) require review.`,
        section: 'criminal',
      });
    }
    if (params.watchlistFindings > 0) {
      items.push({
        severity: 'REVIEW',
        title: 'Watch List Hits Found',
        description: `${params.watchlistCount} watch list hit(s) require review.`,
        section: 'watchlist',
      });
    }
    if (params.mvrFindings > 0) {
      items.push({
        severity: 'REVIEW',
        title: 'MVR Violations Found',
        description: `${params.mvrCount} motor vehicle violation(s) require review.`,
        section: 'mvr',
      });
    }
    if (params.shouldFlagExpiredLicenses) {
      items.push({
        severity: 'HIGH',
        title: 'Driver\'s License Expired',
        description: `${params.expiredLicensesCount} driver's license record(s) have expired.`,
        section: 'licenses',
      });
    }
    return items.filter((i) => this.isSectionAvailable(i.section));
  }

  private buildSidebarSections(counts: { [id: string]: ISectionCount }): ISidebarSection[] {
    const available = SIDEBAR_TEMPLATE
      .filter((s) => this.isSectionAvailable(s.id))
      .map((s) => Object.assign({}, s));
    available.forEach((section) => {
      const c = counts[section.id];
      if (c) {
        section.count = c.count;
        section.findings = c.findings;
        section.badgeType = this.resolveSidebarBadgeType(section.id, c.count, c.findings);
      }
    });
    return available;
  }

  private resolveSidebarBadgeType(id: string, count: number, findings: number): SidebarBadgeType {
    if (findings > 0) {
      return id === 'identity' ? 'dot' : 'findings';
    }
    return count > 0 ? 'count' : 'none';
  }

  private isGoodOutcome(value: string): boolean {
    if (!value) {
      return false;
    }
    const normalized = value.trim().toUpperCase();
    return normalized === 'CLEAR' || normalized === 'ELIGIBLE';
  }

  private isReviewFinding(count: number): boolean {
    if (this.source === 'CheckR') {
      return count > 0 && !this.isGoodOutcome(this.detail.result);
    }
    return count > 0;
  }

  private isReviewFindingByRecords(count: number, records: { status?: string; result?: string }[]): boolean {
    if (count <= 0) {
      return false;
    }
    if (this.source !== 'CheckR') {
      return true;
    }
    return (records || []).some((r) => {
      const result = r && r.result;
      return !!result && !this.isGoodOutcome(result);
    });
  }

  private isSectionAvailable(id: string): boolean {
    const allowed = SECTION_AVAILABILITY[id];
    return !allowed || !this.source || allowed.indexOf(this.source) !== -1;
  }

  private criminalBadge(classification: string): ICardBadge {
    const normalized = (classification || '').trim().toUpperCase();
    if (!normalized) {
      return { text: 'NO CLASSIFICATION', kind: 'muted' };
    }
    if (normalized === 'FELONY') {
      return { text: classification, kind: 'danger' };
    }
    if (normalized === 'MISDEMEANOR') {
      return { text: classification, kind: 'warn' };
    }
    if (normalized === 'TRAFFIC' || normalized === 'INFRACTION') {
      return { text: classification, kind: 'info' };
    }
    return { text: classification, kind: 'caution' };
  }

  private professionalLicenseBadge(status: string): ICardBadge {
    const normalized = (status || '').trim().toUpperCase();
    if (normalized === 'ACTIVE' || normalized === 'VALID') {
      return { text: status, kind: 'success' };
    }
    if (normalized) {
      return { text: status, kind: 'warn' };
    }
    return { text: 'RECORD', kind: 'info' };
  }

  private hasFullDate(date: IUnifiedDate): boolean {
    return !!(date && date.year && date.month && date.day);
  }

  private isDateExpired(date: IUnifiedDate): boolean {
    if (!this.hasFullDate(date)) {
      return false;
    }
    const expiry = new Date(Number(date.year), Number(date.month) - 1, Number(date.day));
    return expiry.getTime() < new Date().getTime();
  }

  private omitEmptyFields(fields: ICardField[]): ICardField[] {
    return fields.filter((f) => !!f.value && f.value !== '—');
  }

  private joinCardSub(parts: string[]): string {
    const filtered = parts.filter((p) => !!p && p !== '—');
    return filtered.length ? filtered.join(' • ') : '';
  }
}

export const EMPTY_BACKGROUND_CHECK_VIEW_MODEL: IBackgroundCheckViewModel = {
  criminalCards: [],
  driversLicenseCards: [],
  driversLicenseGroups: [],
  sharedSsnCards: [],
  professionalLicenseCards: [],
  watchListCards: [],
  mvrViolationCards: [],
  mvrAccidentCards: [],
  documents: [],
  metrics: [],
  itemsRequiringReview: [],
  subjectMatch: EMPTY_SUBJECT_MATCH,
  identity: {
    names: [], dobs: [], addresses: [], phones: [], lastPhone: undefined, emails: [],
    ssnTrace: '—', ssnHistory: [], isClear: true, individualsSharingSsn: [],
  },
  reportDetails: {
    reportToken: undefined,
    searchType: 'Comprehensive Person Report',
    reportDate: undefined,
    source: 'SearchBug API',
    status: undefined,
    result: undefined,
    assessment: undefined,
    completedAt: undefined,
  },
  sidebarSections: SIDEBAR_TEMPLATE.map((s) => Object.assign({}, s)),
};



// WEBPACK FOOTER //
// ./src/app/utils/driver-background-check/background-check-card.builder.ts